import { useState, useMemo } from "react"
import {
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  IconButton,
  Divider,
  Avatar,
  Skeleton,
  CircularProgress,
} from "@mui/material"
import {
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Inbox as InboxIcon,
  ArrowForward as ArrowForwardIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material"
import { useApiQuery } from "../../hooks/useApi"
import { useNavigate } from "react-router-dom"
import SettlementReportGenerator from "./SettlementReportGenerator"
import ClientBalanceCard from "../../components/ClientBalanceCard"

// Compact SaaS-style metric card with avatar icon
function MetricCard({ title, value, icon, bgColor, iconColor, subtitle }) {
  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        border: "1px solid #f1f5f9",
        borderRadius: "12px",
        p: 2.5,
        height: "100%",
        // boxShadow: "0 1px 3px 0 rgba(15,23,42,0.05)",
        // transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 5px 10px -3px rgba(15,23,42,0.08)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography
            variant="overline"
            sx={{ fontWeight: 600, letterSpacing: "0.06em", color: "#64748b", display: "block", mb: 0.5 }}
          >
            {title}
          </Typography>
          <Typography variant="h3" component="p" sx={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.1, mb: 0.5 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar
          sx={{
            backgroundColor: bgColor,
            color: iconColor,
            width: 52,
            height: 52,
            borderRadius: "10px",
            flexShrink: 0,
          }}
        >
          {icon}
        </Avatar>
      </Box>
    </Box>
  )
}

// Compact action button shortcut
function QuickActionRow({ label, description, icon, onClick, primary }) {
  return (
    <Button
      fullWidth
      variant={primary ? "contained" : "outlined"}
      onClick={onClick}
      startIcon={icon}
      sx={{
        justifyContent: "flex-start",
        textTransform: "none",
        fontWeight: 600,
        borderRadius: "8px",
        py: 1.25,
        px: 2,
        mb: 1.5,
        transition: "all 0.2s",
        ...(primary
          ? {
            backgroundColor: "#10b981",
            color: "#ffffff",
            boxShadow: "0 1px 2px 0 rgba(16,185,129,0.25)",
            "&:hover": {
              backgroundColor: "#059669",
              boxShadow: "none",
            },
          }
          : {
            color: "#334155",
            borderColor: "#e2e8f0",
            backgroundColor: "#ffffff",
            "&:hover": {
              borderColor: "#cbd5e1",
              backgroundColor: "#f8fafc",
              transform: "translateX(2px)",
            },
          }),
      }}
    >
      {label}
    </Button>
  )
}

// Skeleton loader matching the two-column layout
function DashboardSkeleton() {
  return (
    <Box sx={{ py: 1 }}>
      {/* Header skeleton */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Skeleton variant="text" width={220} height={40} />
          <Skeleton variant="text" width={340} height={20} />
        </Box>
        <Stack direction="row" spacing={2}>
          <Skeleton variant="rectangular" width={180} height={40} sx={{ borderRadius: "8px" }} />
          <Skeleton variant="rectangular" width={160} height={40} sx={{ borderRadius: "8px" }} />
        </Stack>
      </Box>

      {/* KPI skeleton */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper sx={{ p: 2.5, borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "none" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ width: "60%" }}>
                  <Skeleton variant="text" width="80%" height={14} />
                  <Skeleton variant="text" width="50%" height={36} />
                  <Skeleton variant="text" width="40%" height={14} />
                </Box>
                <Skeleton variant="rectangular" width={52} height={52} sx={{ borderRadius: "10px" }} />
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Main body skeleton */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "none" }}>
            <Skeleton variant="text" width="30%" height={28} sx={{ mb: 3 }} />
            {[1, 2, 3, 4].map((i) => (
              <Box key={i} sx={{ mb: 2.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Skeleton variant="text" width="25%" height={18} />
                  <Skeleton variant="rectangular" width={80} height={18} sx={{ borderRadius: "8px" }} />
                </Box>
                <Skeleton variant="text" width="55%" height={14} />
                {i < 4 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "none", mb: 3 }}>
            <Skeleton variant="text" width="40%" height={28} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={44} sx={{ borderRadius: "8px", mb: 1.5 }} />
            <Skeleton variant="rectangular" width="100%" height={44} sx={{ borderRadius: "8px", mb: 1.5 }} />
            <Skeleton variant="rectangular" width="100%" height={44} sx={{ borderRadius: "8px" }} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

function FeeSettlerDashboard() {
  const navigate = useNavigate()
  const [dateFilter, setDateFilter] = useState("today")
  const [reportDialogOpen, setReportDialogOpen] = useState(false)

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useApiQuery(["settlement-stats", dateFilter], `/settlements/stats?filter=${dateFilter}`)

  const {
    data: settlements,
    isLoading: settlementsLoading,
    refetch: refetchSettlements,
  } = useApiQuery(["settlements", dateFilter], `/settlements?filter=${dateFilter}`)

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today":
        return "Today"
      case "30days":
        return "Last 30 Days"
      case "month":
        return "This Month"
      case "all":
        return "All Time"
      default:
        return "This Month"
    }
  }

  const handleRefresh = () => {
    refetchStats()
    refetchSettlements()
  }

  // Get recent settlements (last 10)
  const recentSettlements = useMemo(() => {
    if (!settlements) return []
    return settlements.slice(0, 10)
  }, [settlements])

  const isLoading = statsLoading || settlementsLoading

  if (isLoading) return <DashboardSkeleton />

  if (statsError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading dashboard data: {statsError.message}
      </Alert>
    )
  }

  return (
    <Box sx={{ py: 1, minHeight: "90vh" }}>
      {/* ── Header Toolbar ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}
          >
            Fee Settlement
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
            Manage and track fee settlements for completed jobs.
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
          {/* Inline period filter */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              px: 1.5,
              height: 40,
              boxShadow: "0 1px 2px 0 rgba(0,0,0,0.02)",
            }}
          >
            <CalendarIcon sx={{ color: "#94a3b8", fontSize: 18, mr: 1 }} />
            <Typography
              variant="body2"
              sx={{ color: "#475569", fontWeight: 500, mr: 1.5, display: { xs: "none", sm: "inline" } }}
            >
              Filter:
            </Typography>
            <FormControl variant="standard" size="small">
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                disableUnderline
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#1e293b",
                  "& .MuiSelect-select": { py: 0, pr: 3 },
                }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="30days">Last 30 Days</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={handleRefresh}
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  height: 40,
                  width: 40,
                  color: "#475569",
                  "&:hover": { backgroundColor: "#f1f5f9" },
                }}
              >
                <RefreshIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/settle-fees")}
              sx={{
                backgroundColor: "#10b981",
                color: "#ffffff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 700,
                px: 2.5,
                height: 40,
                boxShadow: "0 1px 2px 0 rgba(16,185,129,0.3)",
                "&:hover": {
                  backgroundColor: "#059669",
                  boxShadow: "none",
                },
              }}
            >
              Settle Job Fees
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* ── KPI Cards ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Primary: Amount Settled */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Amount Settled"
            value={`₹${(stats?.totalAmountSettled || 0).toLocaleString()}`}
            icon={<TrendingUpIcon sx={{ fontSize: 26 }} />}
            bgColor="#dcfce7"
            iconColor="#15803d"
            subtitle={`${getFilterLabel()} total`}
          />
        </Grid>

        {/* Secondary: Total Settlements (all time) */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Settlements"
            value={(stats?.totalSettlements || 0).toLocaleString()}
            icon={<CheckCircleIcon sx={{ fontSize: 26 }} />}
            bgColor="#dbeafe"
            iconColor="#1d4ed8"
            subtitle={`${stats?.settlementsInPeriod || 0} ${getFilterLabel().toLowerCase()}`}
          />
        </Grid>

        {/* Attention: Client Balance — uses custom component for dropdown */}
        <Grid item xs={12} sm={6} md={3}>
          <ClientBalanceCard stats={stats} />
        </Grid>

        {/* Supporting: Period Settlements */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Settlements"
            value={stats?.settlementsInPeriod || 0}
            icon={<AssignmentIcon sx={{ fontSize: 26 }} />}
            bgColor="#f0f9ff"
            iconColor="#0369a1"
            subtitle={getFilterLabel()}
          />
        </Grid>
      </Grid>

      {/* ── Main Workspace: 7/5 Split ── */}
      <Grid container spacing={3}>
        {/* Left: Recent Settlements Table */}
        <Grid item xs={12} md={7}>
          <Paper
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 1px 3px 0 rgba(15,23,42,0.05)",
              backgroundColor: "#ffffff",
              minHeight: 420,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b" }}>
                  Recent Settlements
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                  Latest settlement activity for {getFilterLabel().toLowerCase()}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => navigate("/all-settlements")}
                endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: "none", fontWeight: 600, color: "#3b82f6" }}
              >
                View All
              </Button>
            </Box>

            {recentSettlements.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flexGrow: 1,
                  py: 6,
                  px: 2,
                  textAlign: "center",
                }}
              >
                <Avatar sx={{ backgroundColor: "#f8fafc", color: "#94a3b8", width: 72, height: 72, mb: 2 }}>
                  <InboxIcon sx={{ fontSize: 36 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#334155", mb: 0.5 }}>
                  No settlements yet
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 300, mb: 3 }}>
                  {dateFilter !== "all"
                    ? `No settlements recorded for ${getFilterLabel().toLowerCase()}.`
                    : "Start by settling job fees."}
                </Typography>
                <Button
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate("/settle-fees")}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#10b981",
                    borderColor: "#d1fae5",
                    "&:hover": { borderColor: "#a7f3d0", backgroundColor: "#f0fdf4" },
                  }}
                >
                  Settle Job Fees
                </Button>
              </Box>
            ) : (
              <TableContainer sx={{ flexGrow: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600 }}>Client</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600 }}>Job ID</TableCell>
                      <TableCell align="right" sx={{ color: "#64748b", fontWeight: 600 }}>
                        Amount Used
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#64748b", fontWeight: 600 }}>
                        Bal. Carried
                      </TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600 }}>Settled By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentSettlements.map((settlement) => (
                      <TableRow
                        key={settlement._id}
                        hover
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "#475569" }}>
                            {new Date(settlement.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                            {new Date(settlement.createdAt).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                            {settlement.clientId?.name || "N/A"}
                          </Typography>
                          {settlement.clientId?.phoneNumber && (
                            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                              {settlement.clientId.phoneNumber}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={settlement.jobId || "N/A"}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontWeight: 600, fontSize: "0.7rem", height: 20 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#16a34a" }}>
                            ₹{(settlement.totalUsed || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#d97706" }}>
                            ₹{(settlement.balanceCarried || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "#475569" }}>
                            {settlement.settledBy?.name || "N/A"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Right: Quick Actions */}
        <Grid item xs={12} md={5}>
          <Paper
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 1px 3px 0 rgba(15,23,42,0.05)",
              backgroundColor: "#ffffff",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", mb: 2 }}>
              Quick Actions
            </Typography>

            <QuickActionRow
              label="Settle Job Fees"
              icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
              onClick={() => navigate("/settle-fees")}
              primary
            />
            <QuickActionRow
              label="View All Settlements"
              icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
              onClick={() => navigate("/all-settlements")}
            />
            <QuickActionRow
              label="Settlement Report"
              icon={<AssessmentIcon sx={{ fontSize: 18 }} />}
              onClick={() => setReportDialogOpen(true)}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Report Generator Dialog */}
      <SettlementReportGenerator
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
      />
    </Box>
  )
}

export default FeeSettlerDashboard